import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { LinkedPanel } from "../../../components/PortfolioPerformance/linked-panel";
import { Skeleton } from "../../../ui/skeleton";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { GroupedBarChart } from "../../../components/PortfolioPerformance/charts/grouped-bar-chart";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import { BasisToggle, type ThreeWayBasis } from "../../../components/PortfolioPerformance/basis-toggle";
import { IndustrySelect, YearChips } from "../../../components/PortfolioPerformance/chart-filters";
import { cohortColor } from "../lib/api/lifetime-loss-projections";
import { formatCompactCurrency } from "../lib/format";
import {
  useChargeoffCard,
  useLossRecovery,
  useNetLossByVintage,
  useNetLossDistribution,
  useNetLossRecoveryByCohort,
  useNetLossVintage,
} from "../lib/api/loss-recovery";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Toggle a year in a chip selection; an empty selection means every year on offer. */
function toggled(selected: string[], year: string): string[] {
  return selected.includes(year) ? selected.filter((y) => y !== year) : [...selected, year];
}

const ALL_YEARS = "All Years";
const SUMMARY_HEADERS = ["Vintage", "Financed Amount", "Gross Loss", "Recovery", "Net Loss", "Net Loss Rate"];

/** Net Loss Rate by Vintage: one row per funded year, with a Realized / Projected / Active toggle and a year filter on the top right. */
function NetLossByVintageTable() {
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const [year, setYear] = useState<string>(ALL_YEARS);
  const summary = useNetLossByVintage(basis);
  const all = summary.data?.rows ?? [];
  const shown = year === ALL_YEARS ? all : all.filter((r) => r.vintage === year);
  const money = (v: number | null) => (v === null ? "—" : formatCompactCurrency(v));

  return (
    <Panel
      className="mt-6"
      title="Net Loss Rate by Vintage"
      description="Financed amount at month 1, then gross loss, recovery and net loss summed over the vintage's months."
      right={
        <div className="flex flex-wrap items-center gap-2">
          <BasisToggle
            value={basis}
            onChange={(next) => {
              setBasis(next);
              setYear(ALL_YEARS);
            }}
            realizedHint="Realized months of the vintages"
            projectedHint="Projected months of the vintages"
            activeHint="Vintages that still have active contracts"
          />
          <IndustrySelect
            value={year === ALL_YEARS || all.some((r) => r.vintage === year) ? year : ALL_YEARS}
            options={[ALL_YEARS, ...all.map((r) => r.vintage)]}
            onChange={setYear}
            ariaLabel="Vintage year"
            className="w-[130px]"
          />
        </div>
      }
    >
      {summary.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this table.
        </p>
      ) : summary.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : !summary.data?.available ? (
        <p className="text-sm text-muted-foreground">Not Available Yet.</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">No vintages on this side for this ticket size yet.</p>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                {SUMMARY_HEADERS.map((head, i) => (
                  <th
                    key={head}
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${
                      i === 0 ? "text-left" : "text-right"
                    }`}
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.vintage} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2.5 font-medium tabular-nums text-foreground">{r.vintage}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(r.financedAmount)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(r.grossLoss)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(r.recovery)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(r.netLoss)}</td>
                  <td className="px-3 py-2.5 text-right font-medium tabular-nums text-foreground">
                    {r.netLossRate === null ? "—" : `${r.netLossRate.toFixed(2)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** Vintage Analysis of Net Loss: cumulative net loss / financed amount by months on book, one line per funded year. */
function NetLossVintageChart() {
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const [selected, setSelected] = useState<string[]>([]);
  const vintage = useNetLossVintage(basis);
  const rows = vintage.data?.curve ?? [];
  const years = Array.from(new Set(rows.flatMap((r) => Object.keys(r).filter((k) => k !== "monthsOnBook")))).sort();
  const active = selected.filter((y) => years.includes(y));
  const visible = active.length ? active : years;

  return (
    <Panel
      title="Vintage Analysis of Net Loss"
      description="Cumulative net loss as a % of the funded year's financed amount, by months on book. Funded years after 2014."
      right={
        <BasisToggle
          value={basis}
          onChange={setBasis}
          realizedHint="Realized months of the funded years"
          projectedHint="Projected months of the funded years"
          activeHint="Funded years that still have active contracts"
        />
      }
    >
      {vintage.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this chart.
        </p>
      ) : vintage.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : !vintage.data?.available ? (
        <p className="text-sm text-muted-foreground">Not Available Yet.</p>
      ) : years.length === 0 ? (
        <p className="text-sm text-muted-foreground">No funded years under this toggle for this ticket size yet.</p>
      ) : (
        <>
          <YearChips years={years} selected={active} onToggle={(y) => setSelected((prev) => toggled(prev, y))} />
          <TrendLineChart
            // Each value is max(0, value): a vintage whose recoveries have run ahead of its charge-offs reads as 0%, never below it. Only this
            // chart does that; the table figures and every other chart keep the real number.
            data={rows.map((r) => ({
              ...Object.fromEntries(
                Object.entries(r).map(([key, value]) => [key, key !== "monthsOnBook" && typeof value === "number" ? Math.max(0, value) : value]),
              ),
              month: String(r.monthsOnBook),
            }))}
            series={visible.map((year) => ({ dataKey: year, name: year, color: cohortColor(years.indexOf(year)) }))}
            suffix="%"
            decimals={2}
            height={260}
          />
        </>
      )}
    </Panel>
  );
}

/** Net Loss and Recovery as % of Charge-offs, by Cohort: one stacked bar per funded year, with a Realized / Projected / Active toggle. */
function NetLossRecoveryCohortChart() {
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const cohorts = useNetLossRecoveryByCohort(basis);

  return (
    <Panel
      className="mt-6"
      title="Net Loss and Recovery as % of Charge-offs, by Cohort"
      right={
        <BasisToggle
          value={basis}
          onChange={setBasis}
          realizedHint="Realized losses of the funded years"
          projectedHint="Projected losses of the funded years"
          activeHint="Funded years that still have active contracts"
        />
      }
    >
      {cohorts.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this chart.
        </p>
      ) : cohorts.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : !cohorts.data?.available ? (
        <p className="text-sm text-muted-foreground">Not Available Yet.</p>
      ) : cohorts.data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No funded years under this toggle for this ticket size yet.</p>
      ) : (
        <GroupedBarChart
          data={cohorts.data.rows}
          categoryKey="year"
          series={[
            { dataKey: "recovery", name: "Recovery", color: "var(--approve)" },
            { dataKey: "netLoss", name: "Net Loss", color: "var(--decline)" },
          ]}
          suffix="%"
          height={280}
          stacked
        />
      )}
    </Panel>
  );
}

/** Net loss distribution: a calendar month's net loss as a % of its funded year's net loss, one line per funded year over Jan-Dec. */
function NetLossDistributionChart() {
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");
  const [selected, setSelected] = useState<string[]>([]);
  const monthly = useNetLossDistribution(basis);
  const points = monthly.data?.points ?? [];
  const years = Array.from(new Set(points.map((p) => p.year))).sort();
  const active = selected.filter((y) => years.includes(y));
  const visible = active.length ? active : years;
  const rows = MONTH_NAMES.map((name, i) => {
    const row: { month: string; [year: string]: string | number } = { month: name };
    for (const p of points) if (p.month === i + 1) row[p.year] = p.rate;
    return row;
  });
  const lookup = new Map(points.map((p) => [`${p.year}|${MONTH_NAMES[p.month - 1]}`, p]));

  return (
    <Panel
      title="Net loss distribution"
      description="Net Loss distribution over a calendar months."
      right={
        <BasisToggle
          value={basis}
          onChange={setBasis}
          realizedHint="Realized months of the funded years"
          projectedHint="Projected months of the funded years"
          activeHint="Funded years that still have active contracts"
        />
      }
    >
      {monthly.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this chart.
        </p>
      ) : monthly.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : !monthly.data?.available ? (
        <p className="text-sm text-muted-foreground">Not Available Yet.</p>
      ) : years.length === 0 ? (
        <p className="text-sm text-muted-foreground">No funded years under this toggle for this ticket size yet.</p>
      ) : (
        <>
          <YearChips
            years={years}
            selected={active}
            onToggle={(y) => setSelected((prev) => toggled(prev, y))}
            label="Funded year"
          />
          <TrendLineChart
            data={rows}
            series={visible.map((year) => ({
              dataKey: year,
              name: year,
              color: cohortColor(years.indexOf(year)),
              dots: true,
            }))}
            suffix="%"
            decimals={2}
            height={260}
            valueLabel={({ value, dataKey, label }) => {
              const p = dataKey ? lookup.get(`${dataKey}|${label}`) : undefined;
              return p
                ? `${value.toFixed(2)}% (net loss ${formatCompactCurrency(p.netLoss)} / total net loss ${formatCompactCurrency(p.yearTotal)})`
                : `${value.toFixed(2)}%`;
            }}
          />
        </>
      )}
    </Panel>
  );
}

function CardSpark({ data }: { data: { index: number; value: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        {/* Fit the series, not 0 → max, or an 84–86% LGD line reads as flat. */}
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Area
          type="monotone"
          dataKey="value"
          stroke="var(--amber)"
          strokeWidth={1.5}
          fill="var(--amber)"
          fillOpacity={0.15}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function LossRecoveryPage() {
  const { data, isPending, isError, error } = useLossRecovery();
  // Split from the main query -- its only data source is a full-table scan
  // with no supporting index, so it loads independently instead of holding
  // up the rest of the page.
  const chargeoffCard = useChargeoffCard();

  return (
    <div>
      <PageHeader
        title="Loss & Recovery"
        subtitle="How delinquency, default, and recovery are showing up right now, and whether the portfolio is tracking inside its planned risk appetite."
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load loss & recovery data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <div className="mt-8 grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Net Loss (YTD)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.netLossYtd.current}
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Net Loss (Lifetime)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.netLossRate.current}
              </p>
              {data.netLossRate.sinceYear ? (
                <p className="mt-1 text-xs text-muted-foreground">All vintages, {data.netLossRate.sinceYear} to date</p>
              ) : null}
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Expected Loss ({data.asOfLabel})
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.expectedLoss.current}
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Exposure at Default
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.exposureAtDefault.current}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <NetLossVintageChart />
            <NetLossDistributionChart />
          </div>

          <NetLossRecoveryCohortChart />

          <NetLossByVintageTable />

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {data.cards.lgd ? (
              <LinkedPanel
                to="/portfolio-performance/lgd"
                title="LGD"
                statLabel="Portfolio LGD"
                stat={data.cards.lgd.stat}
                chart={<CardSpark data={data.cards.lgd.sparkline} />}
                footnote="The backbone driver of Expected Loss: recovery-based, by asset and borrower industry."
              />
            ) : (
              <Link
                to="/portfolio-performance/lgd"
                className="flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                  LGD
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  The backbone driver of Expected Loss: recovery-based, by asset and borrower
                  industry.
                </p>
                <div className="mt-4 flex flex-1 items-center justify-center py-6">
                  <p className="text-xs text-muted-foreground">Not Available Yet.</p>
                </div>
              </Link>
            )}
            <LinkedPanel
              to="/portfolio-performance/expected-loss"
              title="Expected Loss"
              statLabel="Expected Loss ($)"
              stat={data.cards.expectedLoss.stat}
              chart={<CardSpark data={data.cards.expectedLoss.sparkline} />}
              footnote="Actual PD by month, projected PD for immature vintages, risk appetite line."
            />
            <LinkedPanel
              to="/portfolio-performance/loss-calibration"
              title="Loss Calibration"
              statLabel="Credibility k (default)"
              stat="180"
              chart={<CardSpark data={data.cards.lifetimeLossProjections.sparkline} />}
              footnote="How thin segments are pooled, and what k is set to -- Segment Credibility, k Governance, and Loss Development by funded-year cohort."
            />
            <LinkedPanel
              to="/portfolio-performance/annualized-loss"
              title="Annualized Loss"
              statLabel="Annualized Loss %"
              stat={data.cards.annualizedLoss.stat}
              chart={<CardSpark data={data.cards.annualizedLoss.sparkline} />}
              footnote="Disambiguates per-term analyzed loss from the true annualized rate."
            />
            <LinkedPanel
              to="/portfolio-performance/loss-development-wal"
              title="Weighted Average Life"
              statLabel="Portfolio WAL"
              stat={data.cards.lossDevelopmentWal.stat}
              chart={<CardSpark data={data.cards.lossDevelopmentWal.sparkline} />}
              footnote="RBP-style actual-vs-projected loss development comparison."
            />
            {chargeoffCard.data ? (
              <LinkedPanel
                to="/portfolio-performance/chargeoff-acceleration"
                title="Chargeoff Acceleration"
                statLabel="Default Rate"
                stat={chargeoffCard.data.stat}
                chart={<CardSpark data={chargeoffCard.data.sparkline} />}
                footnote="Industry and portfolio curves, by contract count vs. months on book."
              />
            ) : (
              <Link
                to="/portfolio-performance/chargeoff-acceleration"
                className="flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                  Chargeoff Acceleration
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Industry and portfolio curves, by contract count vs. months on book.
                </p>
                <div className="mt-4 flex flex-1 items-center justify-center py-6">
                  {chargeoffCard.isError ? (
                    <p className="text-xs text-muted-foreground">Could not load.</p>
                  ) : (
                    <Skeleton className="h-10 w-full" />
                  )}
                </div>
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
}
